import { createUseStyles } from "react-jss";
import * as Mixins from "../../../theme/Mixins";
import {
    resolveModernGridEvenRowBackground,
    resolveModernGridRowBorderColor,
    resolveModernGridRowBorderRadius,
    resolveModernGridRowHoverBackground,
    resolveModernGridSubHeaderColor,
} from "../../../theme/gridThemeTokens";

export default createUseStyles((theme: any) => ({
    grid: {
        composes: "grid",
        borderCollapse: "separate",
        borderSpacing: 0,
        "& p": {
            margin: 0,
        },
        ...(theme.DynamicCss?.HasGridChanges
            ? {
                marginBottom: "20px",
            }
            : {
                paddingBottom: "20px",
            }),
    },
    choiceGrid: {
        composes: "choice-grid",
        width: "100%",
    },
    gridViewStarRating: {
        composes: "grid-view--star-rating",
        width: "auto",
    },
    gridContainer: {
        float: "left",
        maxWidth: "100%",
    },
    gridBody: {
        composes: "grid__body",
        "& tr:nth-child(even) > th, tr:nth-child(even) > td": theme.DynamicCss?.HasGridChanges
            ? {}
            : {
                borderTopStyle: "solid",
                borderTopColor: theme.ControlBorderColor,
                borderTopWidth: theme.BorderWidth,
                borderBottomStyle: "solid",
                borderBottomColor: theme.ControlBorderColor,
                borderBottomWidth: theme.BorderWidth,
                color: Mixins.foregroundColor(theme, Mixins.controlBackgroundColor(theme)),
                background: Mixins.alpha(theme.ControlBackground, theme.ControlAlpha),
            },
        "& tr > th:first-child, tr > td:first-child": {
            ...(theme.DynamicCss?.HasGridChanges
                ? {}
                : {
                    borderLeftStyle: "solid",
                    borderLeftWidth: theme.BorderWidth,
                }),
            borderTopLeftRadius: Mixins.getRowCornerBorderRadius(theme.BorderRadius),
            borderBottomLeftRadius: Mixins.getRowCornerBorderRadius(theme.BorderRadius),
        },
        "& tr:nth-child(even) > th:first-child, tr:nth-child(even) > td:first-child": theme
            .DynamicCss?.HasGridChanges
            ? {}
            : { borderLeftColor: theme.ControlBorderColor },
        "& tr:nth-child(odd) > th:first-child, tr:nth-child(odd) > td:first-child": theme.DynamicCss
            ?.HasGridChanges
            ? {}
            : { borderLeftColor: "transparent" },
        "& tr > th:last-child, tr > td:last-child": {
            ...(theme.DynamicCss?.HasGridChanges
                ? {}
                : {
                    borderRightStyle: "solid",
                    borderRightWidth: theme.BorderWidth,
                }),
            borderTopRightRadius: Mixins.getRowCornerBorderRadius(theme.BorderRadius),
            borderBottomRightRadius: Mixins.getRowCornerBorderRadius(theme.BorderRadius),
        },
        "& tr:nth-child(even) > th:last-child, tr:nth-child(even) > td:last-child": theme.DynamicCss
            ?.HasGridChanges
            ? {}
            : { borderRightColor: theme.ControlBorderColor },
        "& tr:nth-child(odd) > th:last-child, tr:nth-child(odd) > td:last-child": theme.DynamicCss
            ?.HasGridChanges
            ? {}
            : { borderRightColor: "transparent" },
    },
    // Image Choice Grid: keep the gridBody row background but drop the default
    // control border color so image cells aren't boxed by row borders. Applied
    // alongside gridBody; && doubles this class in the selector so the override
    // wins by specificity, not by declaration order relative to gridBody.
    gridBodyImageChoice: theme.DynamicCss?.HasGridChanges
        ? {}
        : {
            "&& tr:nth-child(even) > th, && tr:nth-child(even) > td": {
                borderTopColor: "transparent",
                borderBottomColor: "transparent",
            },
            "&& tr:nth-child(even) > th:first-child, && tr:nth-child(even) > td:first-child": {
                borderLeftColor: "transparent",
            },
            "&& tr:nth-child(even) > th:last-child, && tr:nth-child(even) > td:last-child": {
                borderRightColor: "transparent",
            },
        },
    gridRow: {
        composes: "grid__row",
    },
    gridSummary: {
        composes: "grid__summary",
        display: "block",
        textAlign: "right",
        background: theme.DynamicCss?.HasGridChanges ? {} : theme.PrimaryColor,
        border: theme.DynamicCss?.HasGridChanges
            ? {}
            : [[theme.BorderWidth, "solid", Mixins.borderColor(theme, theme.PrimaryColor)]],
        borderBottom:
            theme.ControlAlpha === 0 && theme.BorderWidth === 0
                ? [[3, "solid", Mixins.borderColor(theme, theme.PrimaryColor)]]
                : "",
        borderRadius: Math.min(theme.BorderRadius, theme.InputHeight / 2),
        lineHeight: `${theme.InputHeight + theme.BorderWidth * 2}px`,
        height: theme.InputHeight + theme.BorderWidth * 2,
        boxSizing: "border-box",
        padding: [[0, 16]],
        color: theme.DynamicCss?.HasGridChanges
            ? {}
            : Mixins.foregroundColor(theme, theme.PrimaryColor),
    },
    label: {
        composes: "label",
    },
    labelLeft: {
        composes: "label-left",
        marginRight: "5px",
        textAlign: "left",
    },
    labelRight: {
        composes: "label-right",
        marginLeft: "5px",
        textAlign: "left",
    },
    title: {
        fontWeight: "bold",
    },
    titleRunningTotal: {
        fontWeight: theme.UseModernTheme === true ? 400 : "bold",
    },
    titleGridModern: {
        fontWeight: 400,
    },
    gridSubHeaderText: {
        composes: "grid-subheader-text",
        // !important overrides legacy dynamic CSS and inherited header colors.
        color: `${resolveModernGridSubHeaderColor(theme)} !important`,
        "& *": {
            color: "inherit !important",
        },
    },
    gridTableModern: {
        composes: "grid-table--modern",
        borderCollapse: "collapse",
        "& tr.header-row > th, & tr.header-row > td": {
            borderTop: "none",
            borderLeft: "none",
            borderRight: "none",
            // !important overrides legacy tableRow.ts / tableHeader.ts border rules with !important.
            borderBottomWidth: "1px !important",
            borderBottomStyle: "solid !important",
            borderBottomColor: `${resolveModernGridRowBorderColor()} !important`,
        },
        "& .grid__choice-header": {
            fontWeight: "normal",
        },
    },
    gridDataRowModern: {
        composes: ["grid__row", "data-row"],
        "& > th, & > td": {
            borderTop: "none",
            borderLeft: "none",
            borderRight: "none",
            // !important overrides legacy tableRow.ts border rules with !important.
            borderBottomWidth: "1px !important",
            borderBottomStyle: "solid !important",
            borderBottomColor: `${resolveModernGridRowBorderColor()} !important`,
        },
        "&:hover > th:not(.grid__item--axis-fill), &:hover > td:not(.grid__item--axis-fill)": {
            // !important overrides legacy tableRow.ts hover fills injected with !important.
            backgroundColor: `${resolveModernGridRowHoverBackground()} !important`,
        },
    },
    gridDataRowModernStriped: {
        composes: "data-row--striped",
        "& > th:not(.grid__item--axis-fill), & > td:not(.grid__item--axis-fill)": {
            // !important overrides legacy tableRow.ts duoChrome zebra injected with !important.
            backgroundColor: `${resolveModernGridEvenRowBackground()} !important`,
        },
    },
    collapsibleGrid: {
        composes: "collapsible-grid",
        "& > div:last-child": {
            borderBottom: [[0.5, "solid", "#eeeeee"]],
        },
        ".image-choice__target:hover": {
            boxShadow: "initial",
        },
    },
    collapsibleGridModern: {
        composes: "collapsible-grid--modern",
        borderWidth: 1,
        borderStyle: "solid",
        borderColor: resolveModernGridRowBorderColor(),
        borderRadius: resolveModernGridRowBorderRadius(theme),
        overflow: "hidden",
    },
    collapsibleGridContainer: {
        borderTop: [[0.5, "solid", "#eeeeee"]],
    },
    collapsibleGridHeader: {
        composes: "collapsible-grid__header",
        display: "flex",
        flexDirection: "row",
        padding: [[15, 0, 15, 0]],
        fontWeight: 700,
        cursor: "pointer",
        "& > .label-left": {
            marginRight: 17,
        },
    },
    collapsibleGridHeaderModern: {
        fontWeight: 400,
        padding: 16,
    },
    collapsibleGridHeaderCollapse: {
        composes: "collapsible-grid__header--collapse",
        "& svg": {
            transform: "rotate(180deg)",
        },
    },
    collapsibleGridArrow: {
        composes: "collapsible-grid__arrow",
        transition: [["transform", "400ms", "ease-in-out"]],
        fontSize: Mixins.getFontSize(theme.TextFontSize),
        margin: "auto",
        marginRight: 12,
    },
    collapsibleGridArrowModern: {
        composes: "collapsible-grid__arrow",
        display: "block",
        flexShrink: 0,
        height: 16,
        margin: "auto",
        marginRight: 12,
        width: 16,
    },
    collapsibleGridSeparator: {
        composes: "collapsible-grid__separator",
        padding: [[15, 0, 15, 0]],
        fontWeight: 700,
    },
    collapsibleGridSeparatorModern: {
        fontWeight: 400,
        padding: 16,
    },
    collapsibleGridFooter: {
        composes: "collapsible-grid__footer",
        padding: [[5, 0, 15, 0]],
    },
    choiceList: {
        composes: "choice-list",
    },
    /** Owns split-cell HybridGrid radios so each row/column is one radiogroup (not all radios on the page). */
    hybridRadioGroupBridge: {
        composes: "sr-only",
    },
}));
